import ArrowForward from '@mui/icons-material/ArrowForward';
import DoNotDisturbOn from '@mui/icons-material/DoNotDisturbOn';
import Print from '@mui/icons-material/Print';
import { Box, Button, CircularProgress, IconButton, SxProps, Tooltip, Typography } from '@mui/material';
import { FC } from 'react';
import { useNavigate } from 'react-router';

import { CommonStyles, StyledIcon, sxMR0 } from '@components/Capture/icons/styles';
import { usePrint } from '@hooks/usePrint';
import RefreshError, { errors } from '@uiKit/RefreshError';
import { administrationRoutes } from 'src/routes/paths';

import { tooltips } from '../helper';
import { CapturePiece } from '../types';

interface ILocationIconProps {
  capturePiece: CapturePiece | null;
}

type ErrorInstanceProps = {
  instanceError: boolean;
  refetchInstances: () => void;
  isLoading: boolean;
  printError?: string | null;
  isConfigError: boolean;
};

const ErrorInstance: FC<ErrorInstanceProps> = ({
  instanceError,
  refetchInstances,
  isLoading,
  printError,
  isConfigError,
}) => {
  const navigate = useNavigate();
  if (instanceError) {
    const Component = errors.noInstance;
    return (
      <RefreshError onClick={refetchInstances} type="retry" textColor="text.primary" isLoading={isLoading}>
        <Component />
      </RefreshError>
    );
  }

  if (isConfigError) {
    return (
      <>
        <Box sx={{ textAlign: 'left' }}>
          <Typography variant="body2" sx={{ mx: 1, my: 2 }}>
            Please select a printer and label template to print labels.
          </Typography>
        </Box>
        <Button
          fullWidth
          variant="soft"
          size="large"
          endIcon={<ArrowForward />}
          sx={{ textTransform: 'capitalize' }}
          onClick={() => navigate(administrationRoutes.mailroom('printing'))}
        >
          Manage Label Printing
        </Button>
      </>
    );
  }

  if (printError) {
    const Component = errors.offline(printError as string);
    return (
      <RefreshError onClick={refetchInstances} textColor="text.primary" isLoading={isLoading}>
        <Component />
      </RefreshError>
    );
  }
  return tooltips.Print;
};

const PrintIcon: FC<ILocationIconProps> = ({ capturePiece }) => {
  const {
    print,
    isLoading,
    instanceError,
    refetchInstances,
    printError,
    currentPrinter,
    refreshPrinter,
    isConfigError,
  } = usePrint();

  const isDisabled = Boolean(
    !currentPrinter ||
      printError ||
      instanceError ||
      !capturePiece?.piece?.id ||
      !capturePiece.piece.recipient,
  );

  const onPrintClick = () => {
    if (!isDisabled) {
      print(capturePiece?.piece?.id);
    }
  };
  return (
    <Tooltip
      title={
        <ErrorInstance
          instanceError={instanceError}
          refetchInstances={instanceError ? refetchInstances : refreshPrinter}
          isLoading={isLoading}
          printError={printError}
          isConfigError={isConfigError}
        />
      }
      arrow
    >
      <Box component="span" position="relative">
        <IconButton
          onClick={onPrintClick}
          disabled={isDisabled}
          aria-label={tooltips.Print}
          sx={{ ...CommonStyles, ...sxMR0 } as SxProps}
        >
          <Print sx={StyledIcon(isDisabled ? 'action.disabled' : undefined)} />
          {isLoading && <CircularProgress />}
        </IconButton>
        {(instanceError || printError) && (
          <DoNotDisturbOn
            sx={{ position: 'absolute', width: 10, height: 10, fill: '#FF4E2B', bottom: 10, right: 10 }}
          />
        )}
      </Box>
    </Tooltip>
  );
};

export { PrintIcon };
